<template>
  <div class="business-addresses-groups">
    <v-row class="mx-0" v-if="groupsTotalCount > 0">
      <v-col cols="12" lg="6" class="groups">
        <header class="groups__header">
          <p
            class="groups__header-text"
            :style="`border-color: ${colors.ACCENT}`"
          >
            {{ $t("message.businessAddresses.groups.createdGroups") }}
          </p>
          <div class="d-flex align-center">
            <ui-button
              color="primary"
              icon="icon-plus"
              outlined
              @click="handleAddOrEditGroupBtn()"
            >
              {{ $t("message.businessAddresses.groups.noGroups.button") }}
            </ui-button>
            <v-menu left bottom offset-y :nudge-top="-12">
              <template #activator="{ on }">
                <v-icon color="primary" class="ml-3" v-on="on"
                  >icon-more
                </v-icon>
              </template>
              <ui-button
                @click="handleAddOrEditGroupBtn(null, true)"
                icon="icon-plus"
                iconColor="red"
              >
                {{ $t("message.businessAddresses.groups.labelGroups.addBtn") }}
              </ui-button>
            </v-menu>
          </div>
        </header>
        <div class="full-width mb-4">
          <Pagination
            class="flex-grow-1"
            :page="groupsList.page"
            :per-page="groupsList.perPage"
            :options="groupsList.perPageOptions"
            :total-count="groupsTotalCount"
            @change="handleGroupsListPaginationChange"
          ></Pagination>
        </div>
        <v-list ref="groups-list" class="groups__list">
          <v-list-item
            v-for="group in currentPageFacilitiesGroups"
            :key="group.id"
            ripple
            class="group"
            :class="{
              selected: isSelectedGroup(group)
            }"
            @click="selectGroup(group)"
          >
            <span class="group__name"> {{ group.name }} </span>
            <v-tooltip v-if="group.is_label_group" bottom :max-width="400">
              <template v-slot:activator="{ on }">
                <v-icon
                  class="ml-2 rotate-135"
                  v-on="on"
                  color="primary"
                  size="17.5"
                >
                  mdi-label-outline
                </v-icon>
              </template>
              {{
                $t(
                  "message.businessAddresses.groups.labelGroups.labelGroupListItemTooltip"
                )
              }}
            </v-tooltip>

            <v-list-item-action class="group__actions">
              <span class="group__facilities-count">{{
                getFacilitiesCountLabel(group.facilities_count)
              }}</span>
              <v-icon
                @click.stop="handleAddOrEditGroupBtn(group)"
                class="group__edit"
              >
                icon-edit
              </v-icon>
              <v-menu left :offset-x="true" origin="left top" nudge-top="11">
                <template #activator="{ on }">
                  <v-icon class="group__more-icon" v-on="on">icon-more</v-icon>
                </template>
                <ui-button
                  @click="deleteGroup(group)"
                  icon="icon-trash"
                  iconColor="red"
                >
                  {{ $t("message.common.delete") }}
                </ui-button>
              </v-menu>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <SectionLoader :status="loadingGroupsList" />
      </v-col>

      <v-col cols="12" lg="6" class="facilities">
        <header
          class="facilities__header"
          v-if="hasFacilitiesInSelectedGroup || selectedGroup"
        >
          <p
            class="facilities__header-text"
            :style="`border-color: ${colors.ACCENT}`"
          >
            {{
              $t(
                "message.businessAddresses.groups.facilities.facilitiesInGroup"
              )
            }}
            <v-tooltip v-if="isLabelGroupSelected" bottom :max-width="400">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" color="accent" size="17.5">
                  mdi-information
                </v-icon>
              </template>
              {{
                $t(
                  "message.businessAddresses.groups.labelGroups.labelGroupTooltip"
                )
              }}
            </v-tooltip>
          </p>
          <ui-button
            v-if="!isLabelGroupSelected"
            color="primary"
            icon="icon-plus"
            outlined
            @click="handleAddFacilitiesToGroupBtn"
          >
            {{
              $t(
                "message.businessAddresses.groups.facilities.addFacilitiesButton"
              )
            }}
          </ui-button>
        </header>
        <div
          v-if="
            hasFacilitiesInSelectedGroup || groupFacilitiesList.searchInputValue
          "
        >
          <div class="full-width mb-4">
            <v-text-field
              v-model="groupFacilitiesList.searchInputValue"
              :placeholder="
                $t(
                  'message.businessAddresses.groups.facilities.filterInputPlaceholder'
                )
              "
              prepend-inner-icon="icon-search"
              clearable
              outlined
              rounded
              :hide-details="true"
              @input="onFacilitySearch"
              class="mb-4"
            ></v-text-field>

            <Pagination
              class="flex-grow-1"
              :page="groupFacilitiesList.page"
              :per-page="groupFacilitiesList.perPage"
              :options="groupFacilitiesList.perPageOptions"
              :total-count="selectedGroupFacilitiesTotalCount"
              @change="handleGroupFacilitiesListPaginationChange"
            ></Pagination>
          </div>
          <v-list v-if="hasFacilitiesInSelectedGroup" class="facilities__list">
            <v-list-item
              v-if="!isLabelGroupSelected"
              ripple
              @click="toggleAllSelectedGroupFacilities"
            >
              <v-checkbox
                :inputValue="isAllSelectedGroupFacilitiesSelected"
              ></v-checkbox>
              <v-list-item-content class="select-all-on-page">
                <div class="d-flex align-center mb-0">
                  <v-list-item-title class="my-auto mr-4">
                    <p>
                      {{ $t("message.common.selectAll") }}
                    </p>
                  </v-list-item-title>

                  <ui-button
                    v-show="selectedGroupSelectedFacilitiesCount > 0"
                    @click.stop="deleteSelectedGroupSelectedFacilities"
                    icon="icon-trash"
                    iconColor="red"
                  >
                    {{ $t("message.common.delete") }}
                  </ui-button>
                </div>

                <ItemsCounter :count="selectedGroupSelectedFacilitiesCount" />
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-for="item in currentPageSelectedGroupFacilities"
              :key="item.id"
              ripple
              @click="toggleFacility(item)"
            >
              <v-checkbox
                v-if="!isLabelGroupSelected"
                :inputValue="isFacilitySelected(item.id)"
              ></v-checkbox>
              <IsOwnerIndicator
                :isOwner="item.facilityListings.isOwner"
                class="ml-1"
              />
              <v-list-item-content class="d-flex flex-column justify-center">
                <v-list-item-title>
                  <p class="list-item__name">{{ item.name }}</p>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <p class="list-item__description">
                    {{ item.formattedAddress }}
                  </p>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="!isLabelGroupSelected">
                <v-menu left :offset-x="true" origin="left top" nudge-top="11">
                  <template #activator="{ on }">
                    <v-icon class="group__more-icon" v-on="on"
                      >icon-more
                    </v-icon>
                  </template>
                  <ui-button
                    @click="deleteSingleFacilityFromGroup(item)"
                    icon="icon-trash"
                    iconColor="red"
                  >
                    {{ $t("message.common.delete") }}
                  </ui-button>
                </v-menu>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <div v-else-if="groupFacilitiesList.searchInputValue">
            {{
              $t(
                "message.businessAddresses.groups.facilities.noFacilitiesMatchingQuery"
              )
            }}
          </div>

          <Pagination
            class="flex-grow-1"
            :page="groupFacilitiesList.page"
            :per-page="groupFacilitiesList.perPage"
            :options="groupFacilitiesList.perPageOptions"
            :total-count="selectedGroupFacilitiesTotalCount"
            :showOptions="false"
            @change="handleGroupFacilitiesListPaginationChange"
          ></Pagination>
        </div>
        <div v-else-if="selectedGroup" class="no-facilities">
          <img
            src="@/static/businessAddresses/emptyGroup.svg"
            alt="Empty group."
          />
          <p class="no-facilities__description">
            {{ $t("message.businessAddresses.groups.facilities.emptyGroup") }}
          </p>
        </div>

        <div v-else>
          <!-- Theoretically impossible situation, but nice to have a fallback anyway -->
          {{ $t("message.businessAddresses.groups.groupNotSelected") }}
        </div>

        <SectionLoader :status="loadingGroupFacilitiesList" />
      </v-col>
    </v-row>

    <v-row v-else-if="groupsTotalCount === 0">
      <v-col cols="12">
        <div class="no-groups">
          <img
            src="@/static/businessAddresses/noGroupsAdded.svg"
            alt="No groups added."
          />
          <p class="no-groups__description">
            {{ $t("message.businessAddresses.groups.noGroups.description") }}
          </p>
          <div class="d-flex align-center">
            <ui-button
              color="primary"
              icon="icon-plus"
              @click="handleAddOrEditGroupBtn()"
            >
              {{ $t("message.businessAddresses.groups.noGroups.button") }}
            </ui-button>
            <v-menu left bottom offset-y :nudge-top="-12">
              <template #activator="{ on }">
                <v-icon color="primary" class="ml-3" v-on="on"
                  >icon-more
                </v-icon>
              </template>
              <ui-button
                @click="handleAddOrEditGroupBtn(null, true)"
                icon="icon-plus"
                iconColor="red"
              >
                {{ $t("message.businessAddresses.groups.labelGroups.addBtn") }}
              </ui-button>
            </v-menu>
          </div>
        </div>
      </v-col>
    </v-row>

    <SectionLoader :status="loading" />

    <AddOrEditGroupDialog
      :isOpen="addOrEditGroupDialogVisible"
      :group="editedGroup"
      :is-adding-label-group="isAddingLabelGroup"
      @close="handleCloseAddOrEditGroupDialog"
      @save="addNewGroup"
      @edit="editGroup"
    >
    </AddOrEditGroupDialog>

    <AddFacilitiesToGroupDialog
      :isOpen="addFacilitiesToGroupDialogVisible"
      :selectedGroup="selectedGroup"
      @close="addFacilitiesToGroupDialogVisible = false"
      @add="addFacilitiesToGroup"
    >
    </AddFacilitiesToGroupDialog>

    <ConfirmationDialog
      :isOpen="confirmationDialog.visible"
      :body="confirmationDialog.body"
      @closeDialog="confirmationDialog.visible = false"
    />
  </div>
</template>

<script>
import SectionLoader from "@/components/Loader/SectionLoader";
import ItemsCounter from "@/components/List/ItemsCounter";
import Pagination from "@/components/List/Pagination";
import AddOrEditGroupDialog from "@/components/BusinessAddresses/AddOrEditGroupDialog";
import AddFacilitiesToGroupDialog from "@/components/BusinessAddresses/AddFacilitiesToGroupDialog";
import ConfirmationDialog from "@/components/ListingsConfiguration/Dialog/Confirmation.vue";
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";

import facilitiesGroupsService from "@/services/facilitiesGroups";
import serviceHandler from "@/helpers/handlers/service";
import { pluralCount } from "@/helpers/helpers";
import { debounce } from "@/helpers/helpers";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { APP_COLORS } from "@/config/app-colors";
import { FACILITY_GROUP_ACTION } from "@/enums/facility-groups-actions.enum";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "BusinessAddressesGroups",
  components: {
    SectionLoader,
    ItemsCounter,
    Pagination,
    AddOrEditGroupDialog,
    AddFacilitiesToGroupDialog,
    ConfirmationDialog,
    IsOwnerIndicator
  },

  data() {
    return {
      editedGroup: null,
      isAddingLabelGroup: false,
      addOrEditGroupDialogVisible: false,
      addFacilitiesToGroupDialogVisible: false,
      confirmationDialog: {
        visible: false,
        body: {
          isError: false,
          title: "",
          text: "",
          hasCancelButton: false,
          hasDeleteButton: false,
          centerTitle: true
        }
      },
      groupsList: {
        page: 1,
        perPage: 100,
        perPageOptions: [5, 10, 25, 50, 100]
      },
      groupFacilitiesList: {
        page: 1,
        perPage: 10,
        perPageOptions: [10, 25, 50, 100, 200],
        searchInputValue: ""
      },
      loading: false,
      loadingGroupsList: false,
      loadingGroupFacilitiesList: false
    };
  },

  computed: {
    ...mapGetters({
      isLabelGroupSelected: "facilitiesGroups/isLabelGroupSelected",
      isSelectedGroup: "facilitiesGroups/isSelectedGroup",
      isSelectedGroupOnCurrentPage:
        "facilitiesGroups/isSelectedGroupOnCurrentPage",
      isFacilitySelected: "facilitiesGroups/isFacilitySelected",
      isAllSelectedGroupFacilitiesSelected:
        "facilitiesGroups/isAllSelectedGroupFacilitiesSelected",
      selectedGroupSelectedFacilitiesCount:
        "facilitiesGroups/selectedGroupSelectedFacilitiesCount"
    }),
    ...mapState({
      groupsTotalCount: state => state.facilitiesGroups.groupsTotalCount,
      currentPageFacilitiesGroups: state =>
        state.facilitiesGroups.currentPageFacilitiesGroups,
      selectedGroup: state => state.facilitiesGroups.selectedGroup || null,
      currentPageSelectedGroupFacilities: state =>
        state.facilitiesGroups.currentPageSelectedGroupFacilities,
      initiallySelectedFacilities: state =>
        state.facilities.initiallySelectedFacilities,
      selectedFacilities: state => state.facilities.selectedFacilities,
      selectedGroupFacilitiesTotalCount: state =>
        state.facilitiesGroups.selectedGroupFacilitiesTotalCount,
      allSelectedGroupFacilitiesIds: state =>
        state.facilitiesGroups.allSelectedGroupFacilitiesIds,
      selectedGroupSelectedFacilities: state =>
        state.facilitiesGroups.selectedGroupSelectedFacilities
    }),
    colors() {
      return APP_COLORS;
    },
    hasFacilitiesInSelectedGroup() {
      return this.currentPageSelectedGroupFacilities.length > 0;
    }
  },

  async created() {
    this.loading = true;

    await this.fetchFacilitiesGroups();
    this.autoSelectGroup();
    this.loading = false;
  },

  destroyed() {
    this.resetStore();
  },

  methods: {
    ...mapActions({
      resetStore: "facilitiesGroups/resetStore",
      fetchGroupFacilities: "facilitiesGroups/fetchGroupFacilities",
      fetchAllSelectedGroupFacilitiesIds:
        "facilitiesGroups/fetchAllSelectedGroupFacilitiesIds",
      setSelectedGroup: "facilitiesGroups/setSelectedGroup",
      toggleFacility: "facilitiesGroups/toggleFacility",
      toggleAllSelectedGroupFacilities:
        "facilitiesGroups/toggleAllSelectedGroupFacilities"
    }),
    handleAddOrEditGroupBtn(group = null, isAddingLabelGroup = false) {
      this.addOrEditGroupDialogVisible = true;
      this.isAddingLabelGroup = isAddingLabelGroup;
      this.editedGroup = group;
    },
    handleCloseAddOrEditGroupDialog() {
      this.addOrEditGroupDialogVisible = false;
      this.isAddingLabelGroup = false;
      this.editedGroup = null;
    },
    handleAddFacilitiesToGroupBtn() {
      this.addFacilitiesToGroupDialogVisible = true;
    },

    // Groups list related logic below
    fetchFacilitiesGroups() {
      return this.$store.dispatch("facilitiesGroups/fetchFacilitiesGroups", {
        page: this.groupsList.page,
        perPage: this.groupsList.perPage
      });
    },

    async addNewGroup(newGroup) {
      this.loading = true;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATION_GROUP_CREATE,
        name: newGroup.name
      });

      const facilityGroupCreated = await serviceHandler(
        facilitiesGroupsService.postFacilitiesGroup(newGroup)
      );

      if (facilityGroupCreated) {
        await this.fetchFacilitiesGroups();

        this.$notify({
          group: "default",
          type: "success",
          text: `${this.$t(
            "message.businessAddresses.groups.addNewGroup.successfullyAdded",
            [facilityGroupCreated.name]
          )}`
        });

        this.selectGroup(facilityGroupCreated);
        this.scrollToGroupById(facilityGroupCreated.id);

        if (!this.isSelectedGroupOnCurrentPage) {
          this.autoSelectGroup();
        }
      }
      this.loading = false;
    },

    async editGroup(updatedGroup) {
      this.loading = true;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATION_GROUP_EDIT,
        action: "name_changed",
        old_name: this.currentPageFacilitiesGroups.find(
          group => group.id === updatedGroup.id
        ).name,
        new_name: updatedGroup.name
      });

      const facilityGroupUpdated = await serviceHandler(
        facilitiesGroupsService.updateFacilitiesGroup(updatedGroup)
      );

      if (facilityGroupUpdated) {
        await this.fetchFacilitiesGroups();

        this.$notify({
          group: "default",
          type: "success",
          text: this.$t(
            "message.businessAddresses.groups.editGroup.successfullyUpdated"
          )
        });
      }

      if (this.selectedGroup && updatedGroup.is_label_group) {
        this.fetchSelectedGroupFacilities();
      }

      this.loading = false;
    },

    async deleteGroup(group) {
      this.loading = true;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATION_GROUP_DELETE,
        name: group.name
      });

      await serviceHandler(
        facilitiesGroupsService.deleteFacilitiesGroup(group.id)
      );

      this.$notify({
        group: "default",
        type: "success",
        text: `${this.$t(
          "message.businessAddresses.groups.groupSuccessfullyDeleted",
          [group.name]
        )}`
      });

      this.groupsList.page = 1; // in order to avoid fetching groups for not existing page
      await this.fetchFacilitiesGroups();

      this.autoSelectGroup();

      this.loading = false;
    },

    async selectGroup(group) {
      this.setSelectedGroup(group);
      this.groupFacilitiesList.searchInputValue = ""; // reset input so that it does not remain from previously selected group

      if (this.selectedGroupSelectedFacilitiesCount > 0) {
        // if there are any already selected facilities in selected group, reset them
        this.$store.dispatch(
          "facilitiesGroups/setSelectedGroupSelectedFacilities",
          []
        );
      }

      if (this.selectedGroup) {
        this.fetchSelectedGroupFacilities();
      }
    },

    autoSelectGroup() {
      if (this.currentPageFacilitiesGroups.length > 0) {
        this.selectGroup(this.currentPageFacilitiesGroups[0]);
      } else {
        this.selectGroup([]);
      }
    },

    getFacilitiesCountLabel(value) {
      return pluralCount("message.pluralForms.facility", value);
    },

    async handleGroupsListPaginationChange({ page, perPage }) {
      this.loadingGroupsList = true;

      this.groupsList.page = page;
      this.groupsList.perPage = perPage;

      await this.fetchFacilitiesGroups();

      if (!this.isSelectedGroupOnCurrentPage) {
        this.autoSelectGroup();
      }

      this.loadingGroupsList = false;
    },

    // Selected group facilities list related logic below
    async addFacilitiesToGroup() {
      this.loadingGroupFacilitiesList = true;

      const facilitiesIds = this.selectedFacilities.map(
        facility => facility.id
      );
      const newFacilitiesIds = this.selectedFacilities
        .filter(
          facility => !this.initiallySelectedFacilities.includes(facility)
        )
        .map(facility => facility.id);

      let facilityGroupUpdated;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATION_GROUP_EDIT,
        name: this.selectedGroup.name,
        action: "locations_add",
        num_locations_added: newFacilitiesIds ? facilitiesIds.length : null
      });

      facilityGroupUpdated = await serviceHandler(
        facilitiesGroupsService.updateFacilitiesGroup({
          id: this.selectedGroup.id,
          facilities: newFacilitiesIds,
          action: FACILITY_GROUP_ACTION.ADD
        })
      );

      if (facilityGroupUpdated) {
        await this.fetchFacilitiesGroups();

        if (this.isSelectedGroup(facilityGroupUpdated)) {
          this.selectGroup(facilityGroupUpdated);
        }

        this.confirmationDialog.body.title = this.$t(
          "message.businessAddresses.groups.facilities.facilitiesAdded",
          [this.selectedGroup.name]
        );

        this.confirmationDialog.visible = true;
      }
      this.loadingGroupFacilitiesList = false;
    },

    async deleteSelectedGroupSelectedFacilities() {
      this.loadingGroupFacilitiesList = true;

      const action =
        this.selectedGroupSelectedFacilities.length ===
        this.selectedGroupFacilitiesTotalCount
          ? FACILITY_GROUP_ACTION.CLEAR
          : FACILITY_GROUP_ACTION.REMOVE;

      const selectedFacilitiesIds = this.selectedGroupSelectedFacilities.map(
        facility => facility.id
      );

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATION_GROUP_EDIT,
        name: this.selectedGroup.name,
        action: "locations_remove",
        num_locations_removed: selectedFacilitiesIds
          ? selectedFacilitiesIds.length
          : null
      });

      const remainingFacilitiesIds = this.allSelectedGroupFacilitiesIds
        .map(facilityIds => facilityIds.id)
        .filter(el => !selectedFacilitiesIds.includes(el));

      let facilityGroupUpdated;

      facilityGroupUpdated = await serviceHandler(
        facilitiesGroupsService.updateFacilitiesGroup({
          id: this.selectedGroup.id,
          action: action,
          ...(action === FACILITY_GROUP_ACTION.REMOVE && {
            facilities: selectedFacilitiesIds
          })
        })
      );

      if (facilityGroupUpdated) {
        await this.fetchFacilitiesGroups();

        if (this.isSelectedGroup(facilityGroupUpdated)) {
          this.groupFacilitiesList.page = 1; // in order to avoid fetching facilities for not existing page
          this.selectGroup(facilityGroupUpdated);
        }

        this.confirmationDialog.body.title = this.$t(
          "message.businessAddresses.groups.facilities.facilitiesDeleted",
          [this.selectedGroup.name]
        );

        this.confirmationDialog.visible = true;
      }

      this.loadingGroupFacilitiesList = false;
    },

    async deleteSingleFacilityFromGroup(facility) {
      this.loadingGroupFacilitiesList = true;

      const facilitiesIds = this.allSelectedGroupFacilitiesIds
        .filter(facilityIds => facilityIds.id !== facility.id)
        .map(facilityIds => facilityIds.id);

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATION_GROUP_EDIT,
        name: this.selectedGroup.name,
        action: "locations_remove",
        num_locations_removed: facility ? 1 : null
      });

      let facilityGroupUpdated;

      facilityGroupUpdated = await serviceHandler(
        facilitiesGroupsService.updateFacilitiesGroup({
          id: this.selectedGroup.id,
          facilities: [facility.id],
          action: FACILITY_GROUP_ACTION.REMOVE
        })
      );

      if (facilityGroupUpdated) {
        await this.fetchFacilitiesGroups();

        if (this.isSelectedGroup(facilityGroupUpdated)) {
          this.groupFacilitiesList.page = 1; // in order to avoid fetching facilities for not existing page
          this.selectGroup(facilityGroupUpdated);
        }

        this.confirmationDialog.body.title = this.$t(
          "message.businessAddresses.groups.facilities.facilityDeleted",
          [facility.name, this.selectedGroup.name]
        );

        this.confirmationDialog.visible = true;
      }

      this.loadingGroupFacilitiesList = false;
    },

    onFacilitySearch: debounce(async function() {
      this.loadingGroupFacilitiesList = true;
      this.page = 1;
      await this.fetchSelectedGroupFacilities();
      this.loadingGroupFacilitiesList = false;
    }, 500),

    async fetchSelectedGroupFacilities() {
      this.loadingGroupFacilitiesList = true;

      await this.fetchGroupFacilities({
        page: this.groupFacilitiesList.page,
        perPage: this.groupFacilitiesList.perPage,
        isOwner: true,
        groups: [this.selectedGroup.id],
        query: this.groupFacilitiesList.searchInputValue
      });
      await this.fetchAllSelectedGroupFacilitiesIds([this.selectedGroup.id]);

      this.loadingGroupFacilitiesList = false;
    },

    async handleGroupFacilitiesListPaginationChange({ page, perPage }) {
      this.groupFacilitiesList.page = page;
      this.groupFacilitiesList.perPage = perPage;
      this.loadingGroupFacilitiesList = true;
      await this.fetchSelectedGroupFacilities();
      this.loadingGroupFacilitiesList = false;
    },

    scrollToGroupById(id) {
      const index = this.currentPageFacilitiesGroups.findIndex(
        group => group.id === id
      );
      const groupsList = this.$refs["groups-list"].$el;
      const groupElemHeight = document.querySelector("div.group").offsetHeight;
      groupsList.scrollTop = index * groupElemHeight;
    }
  }
};

import { mapState, mapGetters, mapActions } from "vuex";
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables.scss";
$list-border-radius: 14px;

.business-addresses-groups {
  height: 100%;

  .groups,
  .facilities {
    padding: $spacing * 3 $spacing * 4;
    min-height: 700px;
    position: relative;
  }

  .groups {
    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      &::after {
        content: "";
        position: absolute;
        top: 50%;
        right: 0;
        width: 1px;
        height: calc(100% - 2 * 24px);
        transform: translateY(-50%);
        background-color: $gray-200;
      }
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 3 * $spacing;

      &-text {
        @include font-size(16px);
        line-height: 32px;
        margin-bottom: 0;
        padding-left: $spacing * 1.5;
        border-left-width: 3px;
        border-left-style: solid;
      }
    }

    &__list {
      list-style: none;
      border: 1px solid $gray-200;
      border-radius: $list-border-radius;
      max-height: 521px;
      padding: 0;
      overflow-y: auto;
    }

    .group {
      padding: $spacing * 2.5 $spacing * 2;
      display: flex;
      align-items: center;
      min-height: unset;
      cursor: pointer;
      transition: background 0.15s ease-in;

      &.selected {
        background-color: rgba($primary, 0.1);
      }

      &:first-child,
      &:first-child::before {
        border-top-left-radius: $list-border-radius;
        border-top-right-radius: $list-border-radius;
      }

      &:last-child,
      &:last-child::before {
        border-bottom-left-radius: $list-border-radius;
        border-bottom-right-radius: $list-border-radius;
      }

      &:not(:last-child) {
        border-bottom: 1px solid $gray-200;
      }

      &__name {
        display: block;
        @include font-size(14px);
      }

      &__actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 0 $spacing 0 auto;
        min-width: max-content;
        padding-left: $spacing * 1.5;
      }

      &__facilities-count {
        @include font-size(12px);
        line-height: 15px;
        color: $text-muted;
        white-space: pre;
      }

      &__edit {
        margin: 0 $spacing * 3.5;
      }

      &__more-icon {
        position: relative;
      }

      &__more-delete {
        background-color: $light;
      }
    }
  }

  .facilities {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: $spacing * 3;

      &-text {
        @include font-size(16px);
        line-height: 32px;
        margin-bottom: 0;
        padding-left: $spacing * 1.5;
        border-left-width: 3px;
        border-left-style: solid;
      }
    }

    &__list {
      max-height: 650px;
      overflow-y: auto;

      .select-all-on-page {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }

    .no-facilities {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      margin: 0 auto;
      padding: 0 $spacing * 6;
      height: calc(100% - 3 * 24px);
      min-height: unset;

      img {
        width: 100%;
        max-width: 520px;
      }

      &__description {
        @include font-size(22px);
        text-align: center;
        margin: $spacing * 5 0 $spacing * 3;
      }
    }

    .v-list-item {
      &:not(:last-child) {
        border-bottom: 1px solid $gray-200;
      }

      &__title,
      &__subtitle {
        align-self: flex-start;
        white-space: unset;

        p {
          margin-bottom: 0;
          @include font-size(14px);
        }
      }

      &__content {
        padding: $spacing 0;
        padding-left: $spacing * 1.5;

        * {
          flex: unset;
        }

        .selected-counter {
          line-height: 19px;
        }
      }

      &__action {
        margin: 0;
        margin-left: $spacing * 2;
      }
    }
  }

  .no-groups {
    @include noData(650px);

    &__description {
      @include font-size(22px);
      text-align: center;
      margin: $spacing * 5 0 $spacing * 3;
    }
  }
}

.rotate-135 {
  transform: rotateZ(-135deg);
}
</style>
